import {
  POST_LOGIN_REDIRECT_KEY,
  USER_TOKEN_PERSIST,
  isBrowser,
} from "@constants/global";
import GroowLoader from "components/Loader/GroowLoader";
import Router from "next/router";
import React, { useEffect } from "react";
import { useUserStoreContext } from "context/userStore/userStore.provider";
import { isSafeInternalRedirect } from "utils/safe-navigation";

const PrivateRoute = ({ children }) => {
  const { user } = useUserStoreContext((store) => ({
    user: store.user,
  }));

  const hasSessionToken =
    isBrowser && Boolean(localStorage.getItem(USER_TOKEN_PERSIST));

  useEffect(() => {
    if (user) return;

    // Guard against the hydration race: the persisted user store rehydrates a
    // tick after first render, so `user` is briefly null even for a logged-in
    // person opening a deep link. If a session token exists, the user is still
    // loading — wait, don't bounce to /login (a truly invalid token is handled
    // by Apollo's onError refresh/logout).
    if (hasSessionToken) return;

    // Genuinely logged out: preserve a *safe* internal destination so login
    // returns the user here (also stash for Microsoft SSO round-trips).
    const safeRedirect = isSafeInternalRedirect(Router.asPath)
      ? Router.asPath
      : null;
    if (isBrowser && safeRedirect) {
      sessionStorage.setItem(POST_LOGIN_REDIRECT_KEY, safeRedirect);
    }
    Router.replace({
      pathname: "/login",
      ...(safeRedirect ? { query: { redirect: safeRedirect } } : {}),
    });
  }, [user, hasSessionToken]);

  // Do not mount protected children while unauthenticated.
  // Token-present + user-null = still hydrating; show the loader until user loads.
  if (!user) {
    if (hasSessionToken) {
      return React.createElement(
        "div",
        {
          className:
            "flex h-screen w-screen items-center justify-center bg-white dark:bg-black",
        },
        React.createElement(GroowLoader, { size: 300 })
      );
    }
    return null;
  }

  return React.createElement(React.Fragment, null, children);
};

export default React.memo(PrivateRoute);
